import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { toast } from "sonner"
import { useAuth } from "@/contexts/AuthContext"
import { useSubscription } from "@/hooks/useSubscription"
import { useAnalytics } from "@/hooks/useAnalytics"
import { saveWizardSubmission, saveDiagnosticInjuries, TrialAlreadyUsedError } from "@/lib/supabase"
import { INJURY_CONSENT_VERSION, PARCOURS_TEXTS } from "./content"
import { DiagnosticHeader } from "./components"
import {
  BLOCK_KEYS, allZonesHaveStatus, blockOf, blockProgress, canContinue, diagnosticReducer, firstScreenOfBlock,
  fromPersisted, initialDiagnosticState, injuriesToSave, injuryNoteToSave, isComplete,
  screenSequence, toPersisted, type BlockIndex, type DiagnosticAction, type DiagnosticState,
} from "./flow"
import {
  EndScreen, ExperienceScreen, FirstNameScreen, GenderScreen, GoalScreen, InjuryScreen,
  InjuryStatusScreen, IntroScreen, LevelScreen, MetricsScreen, PlaceScreen, ZonesScreen,
} from "./screens"
import "./diagnostic.css"

/**
 * Diagnostic v2 (lot 3) — remplace le wizard « 1 / 8 ».
 *
 * Trois blocs (Toi, Ta pratique, Ton objectif), identité de la landing, puis un écran
 * de fin honnête. Plus d'étape « identité » : le prénom vient du compte (demandé à
 * l'inscription), l'email aussi. Plus d'étape « offre » : l'offre appartient à
 * l'abonnement.
 *
 * Le bouton de l'écran de fin enregistre le diagnostic, les blessures (avec accord
 * seulement), puis active la semaine d'essai Autonomie — seul parcours en libre-service
 * tant que le lot 5 (diagnostic avant compte) n'existe pas.
 */

const STORAGE_KEY = "zeyone_diagnostic_v1"
/** Le temps de voir la carte se sélectionner avant de passer à l'écran suivant. */
const ADVANCE_DELAY_MS = 220

type Saved = { userId: string; state: DiagnosticState }

function readSaved(userId: string | undefined): DiagnosticState | null {
  if (!userId) return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Saved>
    if (parsed.userId !== userId) return null
    return fromPersisted(parsed.state)
  } catch {
    return null
  }
}

function writeSaved(userId: string, state: DiagnosticState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, state: toPersisted(state) } satisfies Saved))
  } catch {
    // Navigation privée, stockage plein : le diagnostic fonctionne sans.
  }
}

/** « camille » → « Camille », comme l'accueil et le profil. */
function capitalize(name: string): string {
  return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : name
}

function clearSaved() {
  try { localStorage.removeItem(STORAGE_KEY) } catch { /* rien à faire */ }
}

export function DiagnosticFlow() {
  const navigate = useNavigate()
  const { user, refreshWizardData } = useAuth()
  const { activateTrial } = useSubscription()
  const { track } = useAnalytics()
  const reduceMotion = useReducedMotion()

  // Le prénom est demandé à l'inscription (user_metadata.first_name). Un compte qui
  // n'en a pas voit un écran de plus au début du bloc « Toi ».
  const accountFirstName = typeof user?.user_metadata?.first_name === "string"
    ? (user.user_metadata.first_name as string).trim()
    : ""
  const askFirstName = accountFirstName.length < 2

  const [s, dispatch] = useReducer(diagnosticReducer, undefined, () => {
    const restored = readSaved(user?.id)
    const state = restored ?? initialDiagnosticState(accountFirstName)
    if (!askFirstName) state.firstName = accountFirstName
    if (!screenSequence(state, askFirstName).includes(state.screen)) state.screen = "intro"
    return state
  })

  const [saving, setSaving] = useState(false)
  const [trialBlocked, setTrialBlocked] = useState(false)
  const mounted = useRef(true)
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const advancing = useRef(false)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (advanceTimer.current) clearTimeout(advanceTimer.current)
    }
  }, [])

  // ── Analytics : jamais d'information de blessure ────────────────────────
  const startedTracked = useRef(false)
  useEffect(() => {
    if (startedTracked.current) return
    startedTracked.current = true
    track("diagnostic_started")
  }, [track])

  const completedBlocks = useRef(new Set<BlockIndex>())
  const previous = useRef<DiagnosticState["screen"]>(s.screen)
  useEffect(() => {
    const prev = previous.current
    previous.current = s.screen
    const seq = screenSequence(s, askFirstName)
    const prevBlock = blockOf(prev)
    const movedForward = seq.indexOf(s.screen) > seq.indexOf(prev)
    if (movedForward && prevBlock !== undefined && blockOf(s.screen) !== prevBlock
        && !completedBlocks.current.has(prevBlock)) {
      completedBlocks.current.add(prevBlock)
      track("diagnostic_block_completed", { block: BLOCK_KEYS[prevBlock] })
    }
    window.scrollTo(0, 0)
  }, [s.screen])

  // ── Sauvegarde locale à chaque réponse (sans les blessures) ────────────
  useEffect(() => {
    if (user?.id && !trialBlocked) writeSaved(user.id, s)
  }, [s, user?.id, trialBlocked])

  // ── Navigation ──────────────────────────────────────────────────────────
  const next = useCallback(() => dispatch({ type: "next", askFirstName }), [askFirstName])
  const back = useCallback(() => {
    // Un passage automatique en attente ne doit pas annuler le retour.
    if (advanceTimer.current) clearTimeout(advanceTimer.current)
    advancing.current = false
    setTrialBlocked(false)
    dispatch({ type: "back", askFirstName })
  }, [askFirstName])

  /** Choix simple : on enregistre, on laisse voir la sélection, on avance. */
  const pick = useCallback((action: DiagnosticAction) => {
    if (advancing.current) return
    advancing.current = true
    dispatch(action)
    advanceTimer.current = setTimeout(() => {
      advancing.current = false
      if (mounted.current) dispatch({ type: "next", askFirstName })
    }, reduceMotion ? 60 : ADVANCE_DELAY_MS)
  }, [askFirstName, reduceMotion])

  const footerNext = () => {
    if (!canContinue(s)) {
      // Le bouton reste touchable (aria-disabled) pour pouvoir dire ce qui manque.
      const missing: Partial<Record<DiagnosticState["screen"], string>> = {
        prenom: "Indique ton prénom pour continuer.",
        zones: s.zones.includes("autre") && s.zones.length > 0
          ? "Précise la zone « Autre » pour continuer."
          : "Choisis au moins une zone pour continuer.",
        etat: !allZonesHaveStatus(s)
          ? "Indique où en est chaque blessure pour continuer."
          : "Coche la case d'accord, ou continue sans enregistrer tes blessures.",
      }
      const message = missing[s.screen]
      if (message) toast.info(message)
      return
    }
    if (s.screen === "etat") dispatch({ type: "confirmInjuries" })
    next()
  }

  const skipInjuries = () => {
    dispatch({ type: "skipInjuries" })
    next()
  }

  const editBlock = (block: BlockIndex) => {
    setTrialBlocked(false)
    dispatch({ type: "go", screen: firstScreenOfBlock(block, askFirstName) })
  }

  // ── Fin : enregistrement, blessures, semaine d'essai ────────────────────
  const completedTracked = useRef(false)
  const finish = async () => {
    if (saving) return
    if (!user?.id) {
      toast.error("Session expirée", { description: "Reconnecte-toi pour continuer." })
      return
    }
    if (!isComplete(s) || !s.gender || !s.levelId || !s.place || !s.goalId) {
      toast.error("Il manque une réponse", { description: "Reviens sur tes réponses pour compléter le diagnostic." })
      return
    }
    setSaving(true)
    try {
      await saveWizardSubmission({
        user_id: user.id,
        first_name: s.firstName.trim(),
        email: user.email ?? "",
        gender: s.gender,
        age: s.age,
        height: s.height,
        weight: s.weight,
        place: s.place,
        level_id: s.levelId,
        goal_id: s.goalId,
        // Uniquement au niveau Débutant : le questionnaire n'est pas posé ailleurs.
        beginner_experience: s.levelId === "beginner" ? s.beginnerExperience ?? null : null,
      })

      // Sans accord, rien ne part (la base refuserait de toute façon).
      const injuries = injuriesToSave(s)
      if (injuries) await saveDiagnosticInjuries(INJURY_CONSENT_VERSION, injuries, injuryNoteToSave(s))

      if (!completedTracked.current) {
        completedTracked.current = true
        track("diagnostic_completed", { level_id: s.levelId, goal_id: s.goalId, place: s.place })
      }

      // Un seul appel serveur : l'essai (J8 07:00 Paris) et la première semaine planifiée.
      // Rejouable sans risque : `start_trial()` est idempotente.
      await activateTrial()

      clearSaved()
      // Le diagnostic existe désormais : la route « / » bascule sur l'accueil.
      await refreshWizardData()
      navigate("/dashboard", { replace: true })
    } catch (error) {
      if (error instanceof TrialAlreadyUsedError) {
        clearSaved()
        setTrialBlocked(true)
      } else {
        console.error("[diagnostic] enregistrement échoué :", error)
        toast.error("Une erreur est survenue", {
          description: error instanceof Error ? error.message : "Impossible de finaliser. Réessaie.",
          duration: 8000,
        })
      }
    } finally {
      if (mounted.current) setSaving(false)
    }
  }

  const seeOffers = async () => {
    await refreshWizardData()
    navigate("/dashboard/billing")
  }

  // ── Rendu ───────────────────────────────────────────────────────────────
  const block = blockOf(s.screen)
  const texts = PARCOURS_TEXTS.autonomie!
  const showFooter = s.screen === "prenom" || s.screen === "mesures" || s.screen === "zones" || s.screen === "etat"
  const footerEnabled = canContinue(s)
  const props = { s, dispatch, pick }

  const screen = (() => {
    switch (s.screen) {
      case "intro": return <IntroScreen onStart={next} />
      case "prenom": return <FirstNameScreen {...props} />
      case "genre": return <GenderScreen {...props} />
      case "mesures": return <MetricsScreen {...props} />
      case "niveau": return <LevelScreen {...props} />
      case "experience": return <ExperienceScreen {...props} />
      case "lieu": return <PlaceScreen {...props} />
      case "blessure": return <InjuryScreen {...props} />
      case "zones": return <ZonesScreen {...props} />
      case "etat": return <InjuryStatusScreen {...props} onSkip={skipInjuries} />
      case "objectif": return <GoalScreen {...props} />
      case "fin":
        return (
          <EndScreen
            s={s}
            displayName={capitalize(s.firstName.trim())}
            texts={texts}
            saving={saving}
            trialBlocked={trialBlocked}
            onStart={finish}
            onSeeOffers={seeOffers}
            onEditBlock={editBlock}
          />
        )
    }
  })()

  return (
    <div className="zd">
      {block !== undefined && (
        <DiagnosticHeader block={block} progress={blockProgress(s, askFirstName)} onBack={back} />
      )}
      <main className="zd-body">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={s.screen}
            initial={reduceMotion ? { opacity: 1 } : { x: 24, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={reduceMotion ? { opacity: 1 } : { x: -24, opacity: 0 }}
            transition={reduceMotion ? { duration: 0 } : { type: "spring", duration: 0.3, bounce: 0 }}
            style={{ flex: "1 1 auto", display: "flex", flexDirection: "column" }}
          >
            {screen}
          </motion.div>
        </AnimatePresence>
      </main>
      {showFooter && (
        <div className="zd-footer">
          <button
            type="button"
            className={footerEnabled ? "zd-btn zd-btn-primary" : "zd-btn zd-btn-idle"}
            aria-disabled={!footerEnabled}
            onClick={footerNext}
          >
            Continuer
          </button>
        </div>
      )}
    </div>
  )
}
